import { gql, useQuery } from '@apollo/client';
import { MoreVert } from '@mui/icons-material';
import Button from '@mui/material/Button';
import type { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import { jsonToGraphQLQuery } from 'json-to-graphql-query';
import React from 'react';
import { allConfigs } from 'src/api/configurations';
import CustomLinearProgress from 'src/components/loading/CustomLinearProgress';
import { StyledContextMenu } from 'src/components/menus/StyledContextMenu';
import type { ActionItem } from 'src/constants';
import { DOWNLOAD_HIRES, HIRES_REQUEST, PERMISSONS_TO_ACTIONS_MAP } from 'src/constants';
import { itemPermsQ } from 'src/dynamoQueries/perms';
import { cleanPayload } from 'src/helpers/common';
import { gatherAssetPerms } from 'src/helpers/permissionsHelper';
import type { ActionOption, PermissionMap } from '../../types';

const useStyles = makeStyles((theme: Theme) => ({
  cardMoreActionsButton: {
    zIndex: 1,
    position: 'absolute',
    right: '10px',
    top: '10px',
    cursor: 'pointer',
    '& svg': {
      color: theme.palette.text.primary,
      padding: '2px',
      backgroundColor: theme.palette.background.default,
      borderRadius: '4px',
    },
  },
  button: {
    minWidth: 'auto',
  },
}));

interface BaseActionPermsMenuProps {
  actions: ActionOption[];
  handleMenuItemClick: (option: ActionOption) => void;
  classes: ReturnType<typeof useStyles>;
  onOpen?: () => void;
  isStatic?: boolean;
}

const BaseActionPermsMenu: React.FC<BaseActionPermsMenuProps> = ({
  actions: initialActions,
  handleMenuItemClick,
  classes,
  onOpen,
  isStatic,
}) => {
  const [open, setOpen] = React.useState<boolean>(false);
  const anchorEl = React.useRef<HTMLDivElement | null>(null);

  const handleToggle = (): void => {
    setOpen(!open);
    if (!open && onOpen) {
      onOpen();
    }
  };

  const handleClose = (): void => {
    setOpen(false);
  };

  const { data: downloadHires } = useQuery(allConfigs, {
    variables: {
      optionKey: DOWNLOAD_HIRES,
    },
    fetchPolicy: 'cache-and-network',
  });

  const downloadHiresCleaned = cleanPayload(downloadHires) as Array<{ optionValue?: string }>;

  const filteredActions =
    downloadHiresCleaned[0]?.optionValue !== '1'
      ? initialActions.filter((action) => action.label !== HIRES_REQUEST)
      : initialActions;

  const handleAction = (action: ActionItem): void => {
    const matched = filteredActions.find((a) => a.label === action.label);
    if (matched) {
      handleMenuItemClick(matched);
    }
    handleClose();
  };

  return (
    <div className={classes.cardMoreActionsButton} ref={anchorEl} style={isStatic ? { position: 'static' } : {}}>
      <Button
        id="mediacard-split-button"
        variant="text"
        color="primary"
        size="small"
        aria-controls={open ? 'split-button-menu' : undefined}
        aria-expanded={open ? 'true' : undefined}
        aria-label="select merge strategy"
        aria-haspopup="menu"
        classes={{ root: classes.button }}
        onClick={handleToggle}
        disabled={filteredActions.length === 0}
      >
        <MoreVert />
      </Button>
      <StyledContextMenu
        actions={filteredActions as unknown as ActionItem[]}
        anchorEl={anchorEl.current}
        open={open}
        onClose={handleClose}
        onAction={handleAction}
        placement="bottom-end"
        testIdPrefix="cyp-mc-action"
        id="split-button-menu"
      />
    </div>
  );
};

interface ActionPermsMenuProps {
  itemId: string;
  handleActionClick: (label: ActionOption, itemId: string) => void;
  parentCollectionId?: string | null;
  moreActions?: ActionOption[];
  permissionMap?: PermissionMap | null;
  onOpen?: () => void;
  isStatic?: boolean;
}

const ActionPermsMenu: React.FC<ActionPermsMenuProps> = ({
  itemId,
  handleActionClick,
  parentCollectionId = null,
  moreActions = [],
  permissionMap = null,
  onOpen,
  isStatic,
}) => {
  const classes = useStyles();

  const handleMenuItemClick = (option: ActionOption): void => {
    handleActionClick(option, itemId);
  };

  const buildQuery = () => {
    const q = itemPermsQ(true);
    return gql(jsonToGraphQLQuery(q));
  };

  const getQueryVariables = (): { globalId: string; parentCollectionId?: string } => {
    return parentCollectionId
      ? {
          globalId: itemId,
          parentCollectionId,
        }
      : {
          globalId: itemId,
        };
  };

  const { data, loading, error } = useQuery(buildQuery(), {
    skip: !itemId,
    variables: getQueryVariables(),
  });

  if (loading) return null;
  if (error) return <div>Error! {error.message}</div>;

  const actionsFromPerms = data?.itemPermissions
    ? (gatherAssetPerms(
        data?.itemPermissions as unknown as Parameters<typeof gatherAssetPerms>[0],
        (permissionMap || PERMISSONS_TO_ACTIONS_MAP) as unknown as Parameters<typeof gatherAssetPerms>[1],
      ) as unknown as ActionOption[])
    : [];
  const filteredActions: ActionOption[] = [...actionsFromPerms, ...moreActions];

  return (
    <BaseActionPermsMenu
      actions={filteredActions}
      handleMenuItemClick={handleMenuItemClick}
      classes={classes}
      onOpen={onOpen}
      isStatic={isStatic}
    />
  );
};

export { ActionPermsMenu, BaseActionPermsMenu };
